import type { FormEvent } from "react";
import { useCallback, useState } from "react";
import { createUseStyles } from "react-jss";
import type { UpdateUser, User } from "../../../../common/src";
import { updateUserSchema, userRoleSchema } from "../../../../common/src";
import { useAction } from "../../hooks/useAction";
import type { VetBoatsTheme } from "../../theme";
import { trpc } from "../../trpc";
import { formatTimestamp } from "../../utils/format";
import { Badge } from "../shared/Badge";
import { Button } from "../shared/Button";
import { Field } from "../shared/Field";
import { Notice } from "../shared/Notice";
import { useFormStyles } from "../shared/styles";

const useStyles = createUseStyles((theme: VetBoatsTheme) => ({
  row: {
    padding: "14px 0",
    borderTop: `1px solid ${theme.color.border}`,
    "&:first-child": { borderTop: "none" },
  },
  summary: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    alignItems: "center",
  },
  name: { fontWeight: 700 },
  meta: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    alignItems: "center",
    color: theme.color.muted,
    fontSize: 14,
    marginTop: 4,
  },
  buttons: { display: "flex", gap: 6, flexWrap: "wrap" },
  panel: {
    marginTop: 12,
    padding: 14,
    background: theme.color.background,
    borderRadius: 6,
  },
}));

const StatusBadges = ({ user }: { user: User }) => {
  if (!user.isActive) {
    return <Badge tone="danger">Deactivated</Badge>;
  }
  if (user.status === "invited") {
    return <Badge tone="info">Invitation sent</Badge>;
  }
  if (user.status === "inviteExpired") {
    return <Badge tone="warning">Invitation expired</Badge>;
  }
  if (user.role === "veteran") {
    return user.waiverSignedAt ? (
      <Badge tone="success">{`Agreement signed ${formatTimestamp(user.waiverSignedAt)}`}</Badge>
    ) : (
      <Badge tone="warning">Agreement not signed</Badge>
    );
  }
  return null;
};

interface UserRowProps {
  user: User;
  isSelf: boolean;
  onChanged: () => void;
}

export const UserRow = ({ user, isSelf, onChanged }: UserRowProps) => {
  const classes = useStyles();
  const form = useFormStyles();
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState<UpdateUser>(user);
  const [fieldError, setFieldError] = useState<string | undefined>(undefined);
  const [success, setSuccess] = useState<string | undefined>(undefined);

  const update = useAction(
    useCallback(
      (input: UpdateUser) =>
        trpc.user.update.mutate({ id: user.id, user: input }),
      [user.id]
    )
  );
  const resendInvite = useAction(
    useCallback(() => trpc.user.resendInvite.mutate({ id: user.id }), [user.id])
  );
  const sendReset = useAction(
    useCallback(
      () => trpc.user.sendPasswordReset.mutate({ id: user.id }),
      [user.id]
    )
  );

  const clearMessages = () => {
    setFieldError(undefined);
    setSuccess(undefined);
    update.clearError();
    resendInvite.clearError();
    sendReset.clearError();
  };

  const toggleEdit = () => {
    clearMessages();
    setEdit({
      displayName: user.displayName,
      role: user.role,
      isActive: user.isActive,
    });
    setEditing((v) => !v);
  };

  const saveEdit = async (event: FormEvent) => {
    event.preventDefault();
    const parsed = updateUserSchema.safeParse(edit);
    if (!parsed.success) {
      setFieldError(parsed.error.issues[0]?.message);
      return;
    }
    if (await update.run(parsed.data)) {
      setEditing(false);
      setSuccess("Saved.");
      onChanged();
    }
  };

  const onResendInvite = async () => {
    clearMessages();
    if (await resendInvite.run()) {
      setSuccess(
        `New invitation sent to ${user.email}. Earlier invitation links no longer work.`
      );
      onChanged();
    }
  };

  const onSendReset = async () => {
    clearMessages();
    if (await sendReset.run()) {
      setSuccess(
        `Password reset link sent to ${user.email}. It expires in an hour.`
      );
    }
  };

  const error = update.error ?? resendInvite.error ?? sendReset.error;
  const joined = user.status === "active";
  const neverJoinedName = user.displayName === user.email;

  return (
    <li className={classes.row}>
      <div className={classes.summary}>
        <div>
          <div className={classes.name}>
            {neverJoinedName ? user.email : user.displayName}{" "}
            {isSelf && <Badge tone="info">You</Badge>}
          </div>
          <div className={classes.meta}>
            {!neverJoinedName && <span>{user.email}</span>}
            <Badge tone={user.role === "admin" ? "info" : "neutral"}>
              {user.role === "admin" ? "Admin" : "Veteran"}
            </Badge>
            <StatusBadges user={user} />
          </div>
        </div>
        <div className={classes.buttons}>
          <Button variant="secondary" small onClick={toggleEdit}>
            Edit
          </Button>
          {user.isActive && !joined && (
            <Button
              variant="secondary"
              small
              disabled={resendInvite.pending}
              onClick={() => void onResendInvite()}
            >
              {resendInvite.pending ? "Sending…" : "Resend invite"}
            </Button>
          )}
          {user.isActive && joined && !isSelf && (
            <Button
              variant="secondary"
              small
              disabled={sendReset.pending}
              onClick={() => void onSendReset()}
            >
              {sendReset.pending ? "Sending…" : "Send password reset"}
            </Button>
          )}
        </div>
      </div>
      {success && !editing && (
        <Notice kind="success" onDismiss={() => setSuccess(undefined)}>
          {success}
        </Notice>
      )}
      {error && !editing && (
        <Notice kind="error" onDismiss={clearMessages}>
          {error}
        </Notice>
      )}
      {editing && (
        <form
          className={classes.panel}
          onSubmit={(e) => void saveEdit(e)}
          noValidate
        >
          <div className={form.grid}>
            <Field label="Full name" htmlFor={`edit-name-${user.id}`}>
              <input
                id={`edit-name-${user.id}`}
                className={form.control}
                value={edit.displayName}
                onChange={(e) =>
                  setEdit((v) => ({ ...v, displayName: e.target.value }))
                }
              />
            </Field>
            <Field label="Role" htmlFor={`edit-role-${user.id}`}>
              <select
                id={`edit-role-${user.id}`}
                className={form.control}
                value={edit.role}
                disabled={isSelf}
                onChange={(e) => {
                  const role = userRoleSchema.safeParse(e.target.value);
                  if (role.success) {
                    setEdit((v) => ({ ...v, role: role.data }));
                  }
                }}
              >
                <option value="veteran">Veteran</option>
                <option value="admin">Admin</option>
              </select>
            </Field>
          </div>
          <label className={`${form.checkbox} ${form.spaced}`}>
            <input
              type="checkbox"
              checked={edit.isActive}
              disabled={isSelf}
              onChange={(e) =>
                setEdit((v) => ({ ...v, isActive: e.target.checked }))
              }
            />
            <span>Active (can sign in)</span>
          </label>
          {fieldError && <Notice kind="error">{fieldError}</Notice>}
          {update.error && <Notice kind="error">{update.error}</Notice>}
          <div className={form.actions}>
            <Button type="submit" small disabled={update.pending}>
              {update.pending ? "Saving…" : "Save"}
            </Button>
            <Button variant="ghost" small onClick={toggleEdit}>
              Cancel
            </Button>
          </div>
        </form>
      )}
    </li>
  );
};
